import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export type ServiceCard = {
  title: string;
  description: string;
  image: string;
  href: string;
  comingSoon?: boolean;
};

export function ServiceTile({
  item,
  linkTo,
}: {
  item: ServiceCard;
  linkTo?: { to: string; params?: Record<string, string> };
}) {
  const learnMoreProps = linkTo
    ? { to: linkTo.to, params: linkTo.params }
    : { to: item.href };

  return (
    <div className="group relative flex aspect-[3/4] w-full shrink-0 flex-col overflow-hidden rounded-2xl bg-neutral-900 shadow-lg ring-1 ring-white/10 transition-transform duration-300 hover:-translate-y-1">
      <img
        src={item.image}
        alt={item.title}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
      {/* Full-tile overlay link for the "learn more" action, kept below the button */}
      <Link
        {...(learnMoreProps as { to: string; params?: Record<string, string> })}
        className="absolute inset-0 z-0"
        aria-label={item.title}
      />
      {item.comingSoon && (
        <span className="absolute right-3 top-3 z-10 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur">
          Coming Soon
        </span>
      )}
      <div className="relative z-10 mt-auto flex flex-col gap-2 p-4 text-white sm:p-5">
        <h3 className="font-display text-lg font-semibold leading-tight sm:text-xl lg:text-2xl">{item.title}</h3>
        <p className="line-clamp-2 text-sm text-white/75 sm:line-clamp-3">{item.description}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button
            asChild
            size="sm"
            variant="outline"
            className="h-8 border-white/40 bg-white/5 px-3 text-[11px] font-bold uppercase tracking-wide text-white hover:bg-white hover:text-primary"
          >
            <Link
              {...(learnMoreProps as { to: string; params?: Record<string, string> })}
              onClick={(e) => e.stopPropagation()}
              className="relative z-10 inline-flex items-center gap-1"
            >
              Learn More<span className="sr-only"> about {item.title}</span> <ArrowRight className="h-3 w-3" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

